import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `/api`;

const ScreeningWizard = ({ userData }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalSteps = 4;
  const isTeacher = userData?.user_type === 'teacher';

  const [formData, setFormData] = useState({
    student_name: '',
    teacher_name: '',
    teacher_email: isTeacher ? (userData?.teacher_email || '') : '',
    grade_level: '',
    concern_type: [],
    concern_details: '',
    classroom_grades_impact: '',
    classroom_grades_details: '',
    testing_results_impact: '',
    testing_results_details: '',
    social_communication_impact: '',
    social_communication_details: ''
  });

  const toggleConcernType = (type) => {
    setFormData(prev => {
      const currentTypes = prev.concern_type;
      if (currentTypes.includes(type)) {
        return { ...prev, concern_type: currentTypes.filter(t => t !== type) };
      } else {
        return { ...prev, concern_type: [...currentTypes, type] };
      }
    });
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.student_name.trim()) {
        toast.error('Please enter the student name');
        return;
      }
      if (!formData.teacher_name.trim()) {
        toast.error('Please enter the referring teacher name');
        return;
      }
      if (!formData.grade_level) {
        toast.error('Please select a grade level');
        return;
      }
      if (formData.concern_type.length === 0) {
        toast.error('Please select at least one concern type');
        return;
      }
      if (!formData.concern_details.trim()) {
        toast.error('Please provide additional details about the concern');
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.classroom_grades_impact) {
        toast.error('Please select an impact level');
        return;
      }
      if (formData.classroom_grades_impact === 'significant' && !formData.classroom_grades_details.trim()) {
        toast.error('Please provide data for significant impact on classroom grades');
        return;
      }
    }
    if (currentStep === 3) {
      if (!formData.testing_results_impact) {
        toast.error('Please select an impact level');
        return;
      }
      if (formData.testing_results_impact === 'significant' && !formData.testing_results_details.trim()) {
        toast.error('Please provide data for significant impact on testing results');
        return;
      }
    }
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.social_communication_impact) {
      toast.error('Please select an impact level');
      return;
    }
    if (formData.social_communication_impact === 'significant' && !formData.social_communication_details.trim()) {
      toast.error('Please provide data for significant impact on social communication');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/screening`, {
        teacher_email: formData.teacher_email || '',
        teacher_name: formData.teacher_name,
        student_name: formData.student_name,
        grade_level: formData.grade_level,
        concern_type: formData.concern_type,
        concern_details: formData.concern_details,
        classroom_grades_impact: formData.classroom_grades_impact,
        classroom_grades_details: formData.classroom_grades_details,
        testing_results_impact: formData.testing_results_impact,
        testing_results_details: formData.testing_results_details,
        social_communication_impact: formData.social_communication_impact,
        social_communication_details: formData.social_communication_details
      }, {
        withCredentials: true
      });
      
      toast.success('Screening completed successfully!');
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to save screening. Please try again.');
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center animate-fade-in">
          <button
            data-testid="back-to-dashboard-button"
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-[#595959] hover:text-[#1C1C1C] mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>New Referral</h1>
          <p className="text-sm text-[#595959]">{userData?.organization_name || 'My School'}</p>
        </div>

        <div className="mb-8">
          <div className="h-2 bg-[#F3F4F1] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#2C4C3B] transition-all duration-500 ease-out rounded-full"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-sm text-[#595959]">
            <span>Step {currentStep} of {totalSteps}</span>
            <span>{Math.round(progressPercentage)}% Complete</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 md:p-10 animate-scale-in">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-[#1C1C1C] mb-2">Student Information & Primary Concern</h2>
                <p className="text-base text-[#595959] leading-relaxed">Enter student details and identify your main concerns</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#A3A3A3] mb-2">
                    Student Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    data-testid="student-name-input"
                    value={formData.student_name}
                    onChange={(e) => updateFormData('student_name', e.target.value)}
                    placeholder="e.g. Alex Johnson"
                    className="w-full h-12 px-4 rounded-xl border-2 border-[#E5E5E5] bg-[#FAFAF8] focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#A3A3A3] mb-2">
                    Referring Teacher <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    data-testid="teacher-name-input"
                    value={formData.teacher_name}
                    onChange={(e) => updateFormData('teacher_name', e.target.value)}
                    placeholder="e.g. Mrs. Smith"
                    className="w-full h-12 px-4 rounded-xl border-2 border-[#E5E5E5] bg-[#FAFAF8] focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#A3A3A3] mb-2">
                    Teacher Email (Optional)
                  </label>
                  <input
                    type="email"
                    data-testid="teacher-email-input"
                    value={formData.teacher_email}
                    onChange={(e) => updateFormData('teacher_email', e.target.value)}
                    placeholder="teacher@school.edu"
                    readOnly={isTeacher}
                    className={`w-full h-12 px-4 rounded-xl border-2 border-[#E5E5E5] bg-[#FAFAF8] focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all ${isTeacher ? 'opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#A3A3A3] mb-2">
                    Grade Level <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="grade-level-select"
                    value={formData.grade_level}
                    onChange={(e) => updateFormData('grade_level', e.target.value)}
                    className="w-full h-12 px-4 rounded-xl border-2 border-[#E5E5E5] bg-[#FAFAF8] focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
                  >
                    <option value="">Select Grade</option>
                    <option value="K">Kindergarten</option>
                    <option value="1">1st Grade</option>
                    <option value="2">2nd Grade</option>
                    <option value="3">3rd Grade</option>
                    <option value="4">4th Grade</option>
                    <option value="5">5th Grade</option>
                    <option value="6">6th Grade</option>
                    <option value="7">7th Grade</option>
                    <option value="8">8th Grade</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  data-testid="concern-articulation-button"
                  onClick={() => toggleConcernType('articulation')}
                  className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                    formData.concern_type.includes('articulation')
                      ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                      : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-[#1C1C1C] mb-1">Articulation (Speech Sounds)</h3>
                      <p className="text-sm text-[#595959]">Difficulty producing specific sounds or pronouncing words clearly</p>
                    </div>
                    {formData.concern_type.includes('articulation') && <CheckCircle2 className="w-6 h-6 text-[#2C4C3B]" />}
                  </div>
                </button>

                <button
                  data-testid="concern-language-button"
                  onClick={() => toggleConcernType('language')}
                  className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                    formData.concern_type.includes('language')
                      ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                      : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-[#1C1C1C] mb-1">Language (Understanding/Expression)</h3>
                      <p className="text-sm text-[#595959]">Challenges with vocabulary, grammar, following directions, or expressing ideas</p>
                    </div>
                    {formData.concern_type.includes('language') && <CheckCircle2 className="w-6 h-6 text-[#2C4C3B]" />}
                  </div>
                </button>

                <button
                  data-testid="concern-fluency-button"
                  onClick={() => toggleConcernType('fluency')}
                  className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                    formData.concern_type.includes('fluency')
                      ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                      : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-[#1C1C1C] mb-1">Speaking Fluency (Stuttering)</h3>
                      <p className="text-sm text-[#595959]">Repetitions of sounds, syllables, or words; prolongations; or blocks during speech</p>
                    </div>
                    {formData.concern_type.includes('fluency') && <CheckCircle2 className="w-6 h-6 text-[#2C4C3B]" />}
                  </div>
                </button>

                <button
                  data-testid="concern-other-button"
                  onClick={() => toggleConcernType('other')}
                  className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                    formData.concern_type.includes('other')
                      ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                      : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-[#1C1C1C] mb-1">Other</h3>
                      <p className="text-sm text-[#595959]">Voice quality, social pragmatics, or another speech/language concern</p>
                    </div>
                    {formData.concern_type.includes('other') && <CheckCircle2 className="w-6 h-6 text-[#2C4C3B]" />}
                  </div>
                </button>
              </div>

              {formData.concern_type.length > 0 && (
                <div className="animate-slide-up">
                  <label className="block text-sm font-semibold text-[#A3A3A3] mb-2">
                    Additional Details {formData.concern_type.includes('other') ? <span className="text-red-500">* (Required for "Other")</span> : <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    data-testid="concern-details-textarea"
                    value={formData.concern_details}
                    onChange={(e) => updateFormData('concern_details', e.target.value)}
                    placeholder="Describe specific examples or behaviors you've observed..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E5E5] bg-[#FAFAF8] focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all resize-none"
                  />
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-[#1C1C1C] mb-2">Impact on Classroom Grades</h2>
                <p className="text-base text-[#595959] leading-relaxed">How does this speech or language concern affect the student's classroom performance and grades?</p>
              </div>

              <div className="space-y-3">
                {[
                  { value: 'minimal', label: 'Minimal Impact', desc: 'Rarely affects grades or classroom work' },
                  { value: 'moderate', label: 'Moderate Impact', desc: 'Sometimes affects ability to complete assignments or participate' },
                  { value: 'significant', label: 'Significant Impact', desc: 'Frequently affects grades and classroom performance. Provide data below.' }
                ].map((option) => (
                  <button
                    key={option.value}
                    data-testid={`grades-impact-${option.value}-button`}
                    onClick={() => updateFormData('classroom_grades_impact', option.value)}
                    className={`w-full p-5 rounded-2xl border-2 transition-all text-left ${
                      formData.classroom_grades_impact === option.value
                        ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                        : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-[#1C1C1C] mb-1">{option.label}</h3>
                        <p className="text-sm text-[#595959]">{option.desc}</p>
                      </div>
                      {formData.classroom_grades_impact === option.value && <CheckCircle2 className="w-5 h-5 text-[#2C4C3B]" />}
                    </div>
                  </button>
                ))}
              </div>

              {formData.classroom_grades_impact && (
                <div className="animate-slide-up">
                  <label className="block text-sm font-semibold text-[#A3A3A3] mb-2">
                    Examples {formData.classroom_grades_impact === 'significant' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    data-testid="grades-details-textarea"
                    value={formData.classroom_grades_details}
                    onChange={(e) => updateFormData('classroom_grades_details', e.target.value)}
                    placeholder="Describe specific examples of how grades are affected..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E5E5] bg-[#FAFAF8] focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all resize-none"
                  />
                </div>
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-[#1C1C1C] mb-2">Impact on Testing Results</h2>
                <p className="text-base text-[#595959] leading-relaxed">How does this concern affect the student's performance on standardized tests or classroom assessments?</p>
              </div>

              <div className="space-y-3">
                {[
                  { value: 'minimal', label: 'Minimal Impact', desc: 'Test scores generally reflect knowledge' },
                  { value: 'moderate', label: 'Moderate Impact', desc: 'Sometimes struggles to demonstrate knowledge on tests' },
                  { value: 'significant', label: 'Significant Impact', desc: 'Test scores often lower than expected ability level. Provide data below.' }
                ].map((option) => (
                  <button
                    key={option.value}
                    data-testid={`testing-impact-${option.value}-button`}
                    onClick={() => updateFormData('testing_results_impact', option.value)}
                    className={`w-full p-5 rounded-2xl border-2 transition-all text-left ${
                      formData.testing_results_impact === option.value
                        ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                        : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-[#1C1C1C] mb-1">{option.label}</h3>
                        <p className="text-sm text-[#595959]">{option.desc}</p>
                      </div>
                      {formData.testing_results_impact === option.value && <CheckCircle2 className="w-5 h-5 text-[#2C4C3B]" />}
                    </div>
                  </button>
                ))}
              </div>

              {formData.testing_results_impact && (
                <div className="animate-slide-up">
                  <label className="block text-sm font-semibold text-[#A3A3A3] mb-2">
                    Examples {formData.testing_results_impact === 'significant' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    data-testid="testing-details-textarea"
                    value={formData.testing_results_details}
                    onChange={(e) => updateFormData('testing_results_details', e.target.value)}
                    placeholder="Describe specific examples of testing challenges..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E5E5] bg-[#FAFAF8] focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all resize-none"
                  />
                </div>
              )}
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-[#1C1C1C] mb-2">Impact on Social Communication</h2>
                <p className="text-base text-[#595959] leading-relaxed">How does this concern affect the student's ability to interact with peers and participate in social situations?</p>
              </div>

              <div className="space-y-3">
                {[
                  { value: 'minimal', label: 'Minimal Impact', desc: 'Interacts comfortably with peers' },
                  { value: 'moderate', label: 'Moderate Impact', desc: 'Sometimes hesitant or struggles in social situations' },
                  { value: 'significant', label: 'Significant Impact', desc: 'Often avoids social interactions or experiences difficulty. Provide data below.' }
                ].map((option) => (
                  <button
                    key={option.value}
                    data-testid={`social-impact-${option.value}-button`}
                    onClick={() => updateFormData('social_communication_impact', option.value)}
                    className={`w-full p-5 rounded-2xl border-2 transition-all text-left ${
                      formData.social_communication_impact === option.value
                        ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                        : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-[#1C1C1C] mb-1">{option.label}</h3>
                        <p className="text-sm text-[#595959]">{option.desc}</p>
                      </div>
                      {formData.social_communication_impact === option.value && <CheckCircle2 className="w-5 h-5 text-[#2C4C3B]" />}
                    </div>
                  </button>
                ))}
              </div>

              {formData.social_communication_impact && (
                <div className="animate-slide-up">
                  <label className="block text-sm font-semibold text-[#A3A3A3] mb-2">
                    Examples {formData.social_communication_impact === 'significant' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    data-testid="social-details-textarea"
                    value={formData.social_communication_details}
                    onChange={(e) => updateFormData('social_communication_details', e.target.value)}
                    placeholder="Describe specific examples of social challenges..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E5E5] bg-[#FAFAF8] focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all resize-none"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-8 border-t border-[#E5E5E5]">
            <button
              data-testid="wizard-back-button"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#E5E5E5] hover:bg-[#F3F4F1] transition-all font-medium text-[#595959] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>

            {currentStep < totalSteps ? (
              <button
                data-testid="wizard-next-button"
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium transition-colors"
              >
                Next
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                data-testid="wizard-submit-button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium transition-colors disabled:bg-[#E5E5E5] disabled:text-[#595959] disabled:cursor-default"
              >
                {loading ? 'Submitting...' : 'Complete Referral'}
                <CheckCircle2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreeningWizard;